import { ChangeDetectorRef, Component, ViewChild, ViewContainerRef, ComponentRef, Type, Injector, Renderer2, ElementRef, OnDestroy, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { FLYOUT_DATA } from './flyout-data.token';
import { FlyoutClosable } from './flyout-closable.interface';
import { MatDialogRef } from '@angular/material/dialog';
import { dialogExcludeSelectors } from 'src/app/_helpers/click-outside.directive';

const overlayPanelSelector = '.cdk-overlay-pane:not(.mat-mdc-tooltip-panel)';

@Component({
  standalone: false,
  selector: 'app-flyout-container',
  templateUrl: './flyout-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./flyout-container.component.scss'],
})
export class FlyoutContainerComponent implements OnDestroy {
  @ViewChild('container', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;
  @ViewChild('flyout', { static: true }) flyout!: ElementRef;
  currentComponentRef?: ComponentRef<any>;

  isOpen = false;
  width = '400px';
  closesOnOutsideClick = true;
  private isClosing = false;
  private clickListener: () => void;

  constructor(private renderer: Renderer2, private cdr: ChangeDetectorRef) { }

  open<T>(component: Type<T>, data?: any, width: string = '400px', closesOnOutsideClick: boolean = true): void {
    if (this.clickListener) {
      this.clickListener();
    }

    this.container?.clear();
    this.width = width;
    this.isOpen = true;
    this.closesOnOutsideClick = closesOnOutsideClick;

    const injector = Injector.create({
      providers: [{ provide: FLYOUT_DATA, useValue: data },
      { provide: MatDialogRef, useValue: { close: () => this.close() } }],
      parent: this.container.injector,
    });

    this.currentComponentRef = this.container.createComponent(component, {
      injector,
    });

    setTimeout(() => {
      this.clickListener = this.renderer.listen('document', 'click', (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement) || !target.isConnected) return;
        const clickedInside = this.flyout.nativeElement.contains(target);
        const isExcluded = this.isClickExcluded(target);
        if (this.isOpen && this.closesOnOutsideClick && !clickedInside && !isExcluded) {
          this.close();
        }
      });
    });
  }

  private isClickExcluded(target: HTMLElement): boolean {
    return dialogExcludeSelectors.some(selector => target.closest(selector) !== null);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: Event): void {
    if (!this.isOpen || !this.closesOnOutsideClick || event.defaultPrevented) {
      return;
    }
    if (document.querySelector(overlayPanelSelector)) {
      return;
    }
    this.close();
  }

  close(): void {
    if (this.isClosing) {
      return;
    }
    const guarded = this.currentComponentRef?.instance as Partial<FlyoutClosable> | undefined;
    if (guarded && typeof guarded.canClose === 'function') {
      this.isClosing = true;
      Promise.resolve(guarded.canClose()).then(allowed => {
        this.isClosing = false;
        if (allowed) {
          this.finalizeClose();
        }
        this.cdr.detectChanges();
      }).catch(() => {
        this.isClosing = false;
        this.cdr.detectChanges();
      });
      return;
    }
    this.finalizeClose();
  }

  private finalizeClose(): void {
    this.container.clear();
    this.currentComponentRef = undefined;
    this.isOpen = false;

    if (this.clickListener) {
      this.clickListener();
    }

    // The document click listener's task isn't always one zone.js recognizes as
    // triggering a tick on its own, so isOpen can flip correctly while the [ngClass]
    // binding never re-renders, leaving the flyout visually stuck open. Force it.
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.clickListener) {
      this.clickListener();
    }
  }
}
